import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { investibleStructuresApi } from '@/utils/api';
import type { InvestibleStructure, ApiResponse, OwnerType } from '@/types';

interface InvestibleStructuresState {
  structures: InvestibleStructure[];
  currentStructure: InvestibleStructure | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isValidatingOwner: boolean;
  error: string | null;
  ownerValidationError: string | null;
  filters: {
    planningPeriodId?: string;
    category?: string;
    ownerType?: OwnerType;
    ownerId?: string;
  };
}

const initialState: InvestibleStructuresState = {
  structures: [],
  currentStructure: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isValidatingOwner: false,
  error: null,
  ownerValidationError: null,
  filters: {},
};

// Async thunks
export const fetchInvestibleStructures = createAsyncThunk(
  'investibleStructures/fetchAll',
  async (params?: { planningPeriodId?: string; category?: string; ownerId?: string; skipScopeFilter?: string }, { rejectWithValue }) => {
    const response = await investibleStructuresApi.getAll(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch investible structures');
    }
    return response.data;
  }
);

export const fetchInvestibleStructureById = createAsyncThunk(
  'investibleStructures/fetchById',
  async (id: string, { rejectWithValue }) => {
    const response = await investibleStructuresApi.getById(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch investible structure');
    }
    return response.data;
  }
);

export const createInvestibleStructure = createAsyncThunk(
  'investibleStructures/create',
  async (data: Partial<InvestibleStructure>, { rejectWithValue }) => {
    const response = await investibleStructuresApi.create(data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create investible structure');
    }
    return response.data;
  }
);

export const updateInvestibleStructure = createAsyncThunk(
  'investibleStructures/update',
  async ({ id, data }: { id: string; data: Partial<InvestibleStructure> }, { rejectWithValue }) => {
    const response = await investibleStructuresApi.update(id, data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update investible structure');
    }
    return response.data;
  }
);

export const deleteInvestibleStructure = createAsyncThunk(
  'investibleStructures/delete',
  async (id: string, { rejectWithValue }) => {
    const response = await investibleStructuresApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete investible structure');
    }
    return id;
  }
);

// New thunk for validating owner assignment
export const validateOwnerAssignment = createAsyncThunk(
  'investibleStructures/validateOwner',
  async ({ ownerType, ownerId, ownerText }: { 
    ownerType: OwnerType; 
    ownerId?: string; 
    ownerText?: string; 
  }, { rejectWithValue }) => {
    try {
      // Validate owner assignment consistency
      if (ownerType === 'USER' && !ownerId) {
        return rejectWithValue('User ID is required when owner type is USER');
      }
      if (ownerType === 'TEXT' && !ownerText?.trim()) {
        return rejectWithValue('Owner text is required when owner type is TEXT');
      }
      if (ownerType === 'USER' && ownerText) {
        return rejectWithValue('Cannot specify both user ID and owner text');
      }
      if (ownerType === 'TEXT' && ownerId) {
        return rejectWithValue('Cannot specify both owner text and user ID');
      }
      
      return { valid: true };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Owner validation failed');
    }
  }
);

const investibleStructuresSlice = createSlice({
  name: 'investibleStructures',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOwnerValidationError: (state) => {
      state.ownerValidationError = null;
    },
    setFilters: (state, action: PayloadAction<Partial<InvestibleStructuresState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentStructure: (state, action: PayloadAction<InvestibleStructure | null>) => {
      state.currentStructure = action.payload;
    },
    updateCurrentStructureOwner: (state, action: PayloadAction<{
      ownerType: OwnerType;
      ownerId?: string;
      ownerText?: string;
    }>) => {
      if (state.currentStructure) {
        state.currentStructure.ownerType = action.payload.ownerType;
        state.currentStructure.ownerId = action.payload.ownerId;
        state.currentStructure.ownerText = action.payload.ownerText;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchInvestibleStructures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvestibleStructures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.structures = action.payload.investibleStructures || action.payload;
      })
      .addCase(fetchInvestibleStructures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchInvestibleStructureById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvestibleStructureById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentStructure = action.payload;
      })
      .addCase(fetchInvestibleStructureById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createInvestibleStructure.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createInvestibleStructure.fulfilled, (state, action) => {
        state.isCreating = false;
        // Backend returns { structure: ... }, extract the structure
        const structure = action.payload.structure || action.payload;
        state.structures.unshift(structure);
      })
      .addCase(createInvestibleStructure.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateInvestibleStructure.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateInvestibleStructure.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Backend returns { structure: ... }, extract the structure
        const structure = action.payload.structure || action.payload;
        const index = state.structures.findIndex(s => s.id === structure.id);
        if (index !== -1) {
          state.structures[index] = structure;
        }
        if (state.currentStructure?.id === structure.id) {
          state.currentStructure = structure;
        }
      })
      .addCase(updateInvestibleStructure.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteInvestibleStructure.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteInvestibleStructure.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.structures = state.structures.filter(s => s.id !== action.payload);
        if (state.currentStructure?.id === action.payload) {
          state.currentStructure = null;
        }
      })
      .addCase(deleteInvestibleStructure.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Validate owner assignment
      .addCase(validateOwnerAssignment.pending, (state) => {
        state.isValidatingOwner = true;
        state.ownerValidationError = null;
      })
      .addCase(validateOwnerAssignment.fulfilled, (state) => {
        state.isValidatingOwner = false;
        state.ownerValidationError = null;
      })
      .addCase(validateOwnerAssignment.rejected, (state, action) => {
        state.isValidatingOwner = false;
        state.ownerValidationError = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  clearOwnerValidationError,
  setFilters, 
  clearFilters, 
  setCurrentStructure,
  updateCurrentStructureOwner
} = investibleStructuresSlice.actions;

export default investibleStructuresSlice.reducer;